import { matchPath } from 'react-router'
import { ROUTES } from 'constants/routes'

export const STEP_PAGE = 'page'
export const STEP_BRANCH_FORK = 'branch-fork'

export const GLOBAL_PAGE_ID = 'global'

export const BRANCH_PENDING = 'pending'
export const BRANCH_ACTIVE = 'active'
export const BRANCH_COMPLETED = 'completed'
export const BRANCH_SKIPPED = 'skipped'

export const CARD_PENDING = 'pending'
export const CARD_COMPLETED = 'completed'
export const CARD_SKIPPED = 'skipped'

export const isBranchFork = (step) =>
  step?.type === STEP_BRANCH_FORK || (step?.branches != null && step?.id != null)

export const isPageStep = (step) => step != null && !isBranchFork(step)

const getPageId = (page) => page?.id

export const getCardStatus = (cardProgress, pageId, cardId) => {
  return cardProgress?.[pageId]?.[cardId]
}

const getBranchStatus = (branchProgress, branchId) => branchProgress?.[branchId]?.status

const getForkActiveBranchId = (fork, branchProgress, ctx) => {
  const forkState = branchProgress?.[fork.id]
  if (forkState?.activeBranchId) {
    return forkState.activeBranchId
  }

  if (typeof fork.selectBranch === 'function') {
    return fork.selectBranch(ctx) || null
  }

  return null
}

const isBranchSkipped = (branchProgress, branchId) =>
  getBranchStatus(branchProgress, branchId) === BRANCH_SKIPPED

const isBranchCompleted = (branchProgress, branchId) =>
  getBranchStatus(branchProgress, branchId) === BRANCH_COMPLETED

const isForkClosed = (fork, branchProgress) => {
  const forkStatus = getBranchStatus(branchProgress, fork.id)
  return forkStatus === BRANCH_COMPLETED || forkStatus === BRANCH_SKIPPED
}

const getBranchPages = (branch) =>
  (branch?.steps || []).filter((step) => step != null && !isBranchFork(step))

/** Собирает все page-шаги: top-level pages + pages из всех веток плоских форков. */
export const collectAllPages = (steps = []) => {
  const pages = []

  for (const step of steps.filter(Boolean)) {
    if (isBranchFork(step)) {
      for (const branch of Object.values(step.branches || {})) {
        pages.push(...getBranchPages(branch))
      }
      continue
    }

    pages.push(step)
  }

  return pages
}

const findForkById = (steps, forkId) => {
  for (const step of steps.filter(Boolean)) {
    if (isBranchFork(step) && step.id === forkId) {
      return step
    }
  }

  return null
}

const findParentForkForBranch = (steps, branchId) => {
  for (const step of steps.filter(Boolean)) {
    if (isBranchFork(step) && step.branches?.[branchId]) {
      return step
    }
  }

  return null
}

const findPageBranchPath = (steps, pageId) => {
  for (const step of steps.filter(Boolean)) {
    if (isBranchFork(step)) {
      for (const [branchId, branch] of Object.entries(step.branches || {})) {
        if (getBranchPages(branch).some((page) => getPageId(page) === pageId)) {
          return [{ forkId: step.id, branchId }]
        }
      }
      continue
    }

    if (getPageId(step) === pageId) {
      return []
    }
  }

  return null
}

export const findBranchIdForPage = (steps, pageId, branchProgress = {}, ctx = {}) => {
  const path = findPageBranchPath(steps, pageId)
  if (!path?.length) return null

  const { forkId, branchId } = path[0]
  const fork = findForkById(steps, forkId)
  const activeBranchId = fork ? getForkActiveBranchId(fork, branchProgress, ctx) : null

  if (activeBranchId && activeBranchId !== branchId) {
    return null
  }

  return branchId
}

const isPageInSkippedBranch = (steps, pageId, branchProgress) => {
  const path = findPageBranchPath(steps, pageId)
  if (!path?.length) return false

  const { forkId, branchId } = path[0]
  return isBranchSkipped(branchProgress, branchId) || isForkClosed({ id: forkId }, branchProgress)
}

/** Страницы активного поддерева с учётом branchProgress и selectBranch. */
export const collectActivePages = (steps = [], branchProgress = {}, ctx = {}) => {
  const pages = []

  for (const step of steps.filter(Boolean)) {
    if (isBranchFork(step)) {
      if (isForkClosed(branchProgress, step)) {
        continue
      }

      const branchId = getForkActiveBranchId(step, branchProgress, ctx)
      if (!branchId) {
        break
      }

      const branch = step.branches?.[branchId]
      if (!branch) {
        break
      }

      if (isBranchSkipped(branchProgress, branchId) || isBranchCompleted(branchProgress, branchId)) {
        continue
      }

      pages.push(...getBranchPages(branch))
      continue
    }

    pages.push(step)
  }

  return pages
}

export const getFirstPage = (steps = [], branchProgress = {}, ctx = {}) => {
  const pages = collectActivePages(steps, branchProgress, ctx)
  return pages[0] || null
}

export const findPageIdByCardId = (track, cardId) => {
  if (!cardId || !track) return null

  for (const entry of track.globalCards || []) {
    const card = entry.card || entry
    if (card.id === cardId) {
      return GLOBAL_PAGE_ID
    }
  }

  if (!track?.steps) return null

  for (const page of collectAllPages(track.steps)) {
    if (page.cards?.some((card) => card.id === cardId)) {
      return getPageId(page)
    }
  }

  return null
}

export const markCardAsCompleted = (cardProgress, pageId, cardId) => ({
  ...cardProgress,
  [pageId]: {
    ...cardProgress?.[pageId],
    [cardId]: CARD_COMPLETED,
  },
})

const buildPagesById = (steps = []) => {
  const pagesById = {}

  for (const page of collectAllPages(steps)) {
    const pageId = getPageId(page)
    if (!pageId) continue

    const cards = [...(page.cards || [])]
    const existingIds = new Set(cards.map((card) => card.id))

    for (const cardId of page.allCardIds || []) {
      if (!existingIds.has(cardId)) {
        cards.push({ id: cardId })
        existingIds.add(cardId)
      }
    }

    pagesById[pageId] = { pageId, cards }
  }

  return pagesById
}

const getGlobalCardEntry = (entry) => {
  if (!entry) return null
  if (entry.card) return entry
  if (entry.id) return { card: entry }
  return null
}

const isGlobalCardVisible = (entry, ctx = {}) => {
  const { showWhen } = entry
  if (typeof showWhen === 'function' && !showWhen(ctx)) {
    return false
  }
  return true
}

/**
 * Видимость обычной карточки страницы. Если задан `showWhen(ctx)` и вернул false —
 * карточка пропускается (не показывается и не считается pending). Несколько карточек
 * с взаимоисключающими `showWhen` на одной странице ведут себя как один условный слот.
 */
const isCardVisible = (card, ctx = {}) => {
  const showWhen = card?.showWhen
  if (typeof showWhen === 'function' && !showWhen(ctx)) {
    return false
  }
  return true
}

const getRouteFilters = (entry) => {
  const card = entry?.card || {}
  return {
    includeRoutes: entry.includeRoutes ?? card.includeRoutes,
    excludeRoutes: entry.excludeRoutes ?? card.excludeRoutes,
  }
}

/** Проверка includeRoutes / excludeRoutes. Если оба не заданы — true. */
export const matchesRouteFilters = (pathname, { includeRoutes, excludeRoutes } = {}) => {
  if (!pathname) return false

  const hasInclude = Array.isArray(includeRoutes) && includeRoutes.length > 0
  const hasExclude = Array.isArray(excludeRoutes) && excludeRoutes.length > 0

  if (!hasInclude && !hasExclude) {
    return true
  }

  if (
    hasInclude &&
    !includeRoutes.some((route) => matchPath(pathname, { path: route, exact: true }))
  ) {
    return false
  }

  if (
    hasExclude &&
    excludeRoutes.some((route) => matchPath(pathname, { path: route, exact: true }))
  ) {
    return false
  }

  return true
}

const buildGlobalCardProgress = (track, previousCardProgress = {}) => {
  const pageProgress = {}

  for (const rawEntry of track?.globalCards || []) {
    const entry = getGlobalCardEntry(rawEntry)
    if (!entry?.card?.id) continue

    const prevStatus = getCardStatus(previousCardProgress, GLOBAL_PAGE_ID, entry.card.id)
    pageProgress[entry.card.id] =
      prevStatus === CARD_COMPLETED || prevStatus === CARD_SKIPPED
        ? CARD_COMPLETED
        : CARD_PENDING
  }

  if (!Object.keys(pageProgress).length) {
    return null
  }

  return pageProgress
}

const isCardProgressEqual = (next, previous) => {
  if (next === previous) return true
  if (!next || !previous) return false

  const nextKeys = Object.keys(next)
  const prevKeys = Object.keys(previous)

  if (nextKeys.length !== prevKeys.length) return false

  for (const pageId of nextKeys) {
    const nextPage = next[pageId]
    const prevPage = previous[pageId]

    if (!prevPage) return false

    const cardIds = Object.keys(nextPage)
    if (cardIds.length !== Object.keys(prevPage).length) return false

    for (const cardId of cardIds) {
      if (nextPage[cardId] !== prevPage[cardId]) return false
    }
  }

  return true
}

export const buildCardProgressForTrack = (
  track,
  previousCardProgress = {},
  branchProgress = {},
  ctx = {},
) => {
  if (!track) return previousCardProgress || {}

  const cardProgress = {}

  if (track.steps) {
    for (const { pageId, cards } of Object.values(buildPagesById(track.steps))) {
      const pageProgress = {}
      const skippedBranch = isPageInSkippedBranch(track.steps, pageId, branchProgress)

      for (const card of cards) {
        const prevStatus = getCardStatus(previousCardProgress, pageId, card.id)

        if (skippedBranch) {
          pageProgress[card.id] = CARD_SKIPPED
        } else {
          pageProgress[card.id] =
            prevStatus === CARD_COMPLETED || prevStatus === CARD_SKIPPED
              ? CARD_COMPLETED
              : CARD_PENDING
        }
      }

      cardProgress[pageId] = pageProgress
    }
  }

  const globalPageProgress = buildGlobalCardProgress(track, previousCardProgress)
  if (globalPageProgress) {
    cardProgress[GLOBAL_PAGE_ID] = globalPageProgress
  }

  if (isCardProgressEqual(cardProgress, previousCardProgress)) {
    return previousCardProgress
  }

  return cardProgress
}

export const findFirstPendingCard = (track, cardProgress, branchProgress, ctx = {}) => {
  if (!track?.steps) return null

  for (const page of collectActivePages(track.steps, branchProgress, ctx)) {
    const pageId = getPageId(page)

    for (const card of page.cards || []) {
      if (!isCardVisible(card, ctx)) continue
      const status = getCardStatus(cardProgress, pageId, card.id)
      if (status !== CARD_COMPLETED && status !== CARD_SKIPPED) {
        return {
          card,
          cardId: card.id,
          pageId,
          page,
          branchId: findBranchIdForPage(track.steps, pageId, branchProgress, ctx),
          urlParams: page.urlParams,
        }
      }
    }
  }

  return null
}

const findGlobalCurrentCard = (track, cardProgress, pathname, ctx = {}) => {
  for (const rawEntry of track?.globalCards || []) {
    const entry = getGlobalCardEntry(rawEntry)
    if (!entry?.card) continue
    if (!isGlobalCardVisible(entry, ctx)) continue
    if (!matchesRouteFilters(pathname, getRouteFilters(entry))) continue

    const { card } = entry
    const status = getCardStatus(cardProgress, GLOBAL_PAGE_ID, card.id)
    if (status !== CARD_COMPLETED && status !== CARD_SKIPPED) {
      return {
        card,
        cardId: card.id,
        pageId: GLOBAL_PAGE_ID,
        branchId: null,
        urlParams: null,
        scope: 'global',
      }
    }
  }

  return null
}

export const findCurrentCard = (onboarding, pathname, ctx = {}) => {
  if (!pathname) return null

  const track = onboarding?.tracks?.[onboarding.activeTrackName]
  if (!track) return null

  const mergedCtx = { ...ctx, visitFlags: onboarding.visitFlags || {} }
  const branchProgress = track.steps
    ? syncBranchesFromPathname(track.steps, pathname, onboarding.branchProgress || {}, mergedCtx)
    : onboarding.branchProgress || {}
  const cardProgress = onboarding.cardProgress || {}

  if (track.steps) {
    for (const page of collectActivePages(track.steps, branchProgress, mergedCtx)) {
      if (!page.url || !matchPath(pathname, { path: page.url, exact: true })) {
        continue
      }

      const pageId = getPageId(page)

      for (const card of page.cards || []) {
        if (!isCardVisible(card, mergedCtx)) continue
        const status = getCardStatus(cardProgress, pageId, card.id)
        if (status !== CARD_COMPLETED && status !== CARD_SKIPPED) {
          return {
            card,
            cardId: card.id,
            pageId,
            branchId: findBranchIdForPage(track.steps, pageId, branchProgress, mergedCtx),
            urlParams: page.urlParams,
          }
        }
      }
    }
  }

  return findGlobalCurrentCard(track, cardProgress, pathname, mergedCtx)
}

export const findCurrentCardKey = (onboarding, pathname, ctx = {}) =>
  findCurrentCard(onboarding, pathname, ctx)?.cardId || null

/**
 * Активные слушатели для текущего pathname. Слушатели (`page.listeners`) —
 * пассивные подписчики на внешние redux-события. Они НЕ участвуют в резолве
 * карточек, прогрессе и расчёте завершения трека (живут отдельным полем, не в
 * `cards`), пользователь с ними не взаимодействует. Возвращаем те, чья страница
 * сейчас активна по ветке и совпадает по URL.
 */
export const collectActiveListeners = (onboarding, pathname, ctx = {}) => {
  if (!pathname) return []

  const track = onboarding?.tracks?.[onboarding?.activeTrackName]
  if (!track?.steps) return []

  const mergedCtx = { ...ctx, visitFlags: onboarding.visitFlags || {} }
  const branchProgress = syncBranchesFromPathname(
    track.steps,
    pathname,
    onboarding.branchProgress || {},
    mergedCtx,
  )

  const listeners = []
  for (const page of collectActivePages(track.steps, branchProgress, mergedCtx)) {
    if (!page.listeners?.length || !page.url) continue
    if (!matchPath(pathname, { path: page.url, exact: true })) continue
    listeners.push(...page.listeners)
  }

  return listeners
}

const isNetworkPagePath = (pathname) =>
  Boolean(matchPath(pathname, { path: ROUTES.SERVICES_NETWORK, exact: true }))

export const syncVisitFlags = (pathname, visitFlags = {}) => {
  if (!pathname || visitFlags.networkPageVisited) {
    return visitFlags
  }

  if (!isNetworkPagePath(pathname)) {
    return visitFlags
  }

  return { ...visitFlags, networkPageVisited: true }
}

export const activateForkBranch = (steps, forkId, branchId, branchProgress = {}) => {
  const fork = findForkById(steps, forkId)
  if (!fork?.branches?.[branchId]) return branchProgress

  const forkState = branchProgress[forkId] || {}
  const branchState = branchProgress[branchId] || {}

  if (
    forkState.activeBranchId === branchId &&
    forkState.status === BRANCH_ACTIVE &&
    branchState.status === BRANCH_ACTIVE
  ) {
    return branchProgress
  }

  return {
    ...branchProgress,
    [forkId]: { ...forkState, status: BRANCH_ACTIVE, activeBranchId: branchId },
    [branchId]: { ...branchState, status: BRANCH_ACTIVE },
  }
}

export const syncBranchesFromPathname = (steps, pathname, branchProgress = {}, ctx = {}) => {
  if (!pathname || !steps?.length) return branchProgress

  let nextProgress = { ...branchProgress }
  let changed = false

  for (const step of steps.filter(Boolean)) {
    if (!isBranchFork(step)) continue

    const matchingBranchIds = []

    for (const [branchId, branch] of Object.entries(step.branches || {})) {
      const hasMatch = getBranchPages(branch).some(
        (page) => page.url && matchPath(pathname, { path: page.url, exact: true }),
      )

      if (hasMatch) {
        matchingBranchIds.push(branchId)
      }
    }

    if (matchingBranchIds.length === 0) continue

    let branchId = null

    if (matchingBranchIds.length > 1 && typeof step.selectBranch === 'function') {
      branchId = step.selectBranch(ctx)
    }

    if (!branchId || !matchingBranchIds.includes(branchId)) {
      branchId = getForkActiveBranchId(step, nextProgress, ctx)
    }

    if (!branchId || !matchingBranchIds.includes(branchId)) {
      branchId = matchingBranchIds[0]
    }

    const forkState = nextProgress[step.id] || {}

    if (forkState.activeBranchId !== branchId) {
      nextProgress = {
        ...nextProgress,
        [step.id]: {
          ...forkState,
          status: BRANCH_ACTIVE,
          activeBranchId: branchId,
        },
      }
      changed = true
    } else if (forkState.status !== BRANCH_ACTIVE && forkState.status !== BRANCH_COMPLETED) {
      nextProgress = {
        ...nextProgress,
        [step.id]: { ...forkState, status: BRANCH_ACTIVE },
      }
      changed = true
    }

    const branchState = nextProgress[branchId] || {}
    if (
      branchState.status !== BRANCH_ACTIVE &&
      branchState.status !== BRANCH_COMPLETED &&
      branchState.status !== BRANCH_SKIPPED
    ) {
      nextProgress = {
        ...nextProgress,
        [branchId]: { ...branchState, status: BRANCH_ACTIVE },
      }
      changed = true
    }
  }

  return changed ? nextProgress : branchProgress
}

export const skipSiblingBranches = (fork, activeBranchId, branchProgress = {}) => {
  let nextProgress = { ...branchProgress }

  for (const branchId of Object.keys(fork.branches || {})) {
    if (branchId === activeBranchId) continue
    if (getBranchStatus(nextProgress, branchId) === BRANCH_COMPLETED) continue

    nextProgress = {
      ...nextProgress,
      [branchId]: { status: BRANCH_SKIPPED },
    }
  }

  return nextProgress
}

export const completeBranch = (track, branchId, branchProgress = {}) => {
  let nextProgress = {
    ...branchProgress,
    [branchId]: { ...(branchProgress[branchId] || {}), status: BRANCH_COMPLETED },
  }

  const fork = findParentForkForBranch(track.steps, branchId)
  if (!fork) return nextProgress

  nextProgress = skipSiblingBranches(fork, branchId, nextProgress)
  nextProgress = {
    ...nextProgress,
    [fork.id]: {
      ...(nextProgress[fork.id] || {}),
      status: BRANCH_COMPLETED,
      activeBranchId: branchId,
    },
  }

  return nextProgress
}

const markCardAsSkipped = (cardProgress, pageId, cardId) => ({
  ...cardProgress,
  [pageId]: {
    ...cardProgress?.[pageId],
    [cardId]: CARD_SKIPPED,
  },
})

/** Помечает все pending-карточки как skipped (в т.ч. bypassed при роутинге). */
export const skipAllPendingCards = (cardProgress = {}) => {
  let nextProgress = { ...cardProgress }

  for (const [pageId, pageProgress] of Object.entries(cardProgress)) {
    if (!pageProgress || typeof pageProgress !== 'object') continue

    for (const [cardId, status] of Object.entries(pageProgress)) {
      if (status === CARD_PENDING) {
        nextProgress = markCardAsSkipped(nextProgress, pageId, cardId)
      }
    }
  }

  return nextProgress
}

/** Закрывает все ещё открытые fork/branch верхнего уровня в треке. */
export const closeAllOpenBranches = (track, branchProgress = {}, ctx = {}) => {
  if (!track?.steps) return branchProgress

  let nextProgress = { ...branchProgress }

  for (const step of track.steps.filter(Boolean)) {
    if (!isBranchFork(step)) continue
    if (isForkClosed(step, nextProgress)) continue

    let branchId = getForkActiveBranchId(step, nextProgress, ctx)
    if (!branchId || !step.branches?.[branchId]) {
      branchId = Object.keys(step.branches || {})[0]
    }

    if (branchId) {
      nextProgress = completeBranch(track, branchId, nextProgress)
    }
  }

  return nextProgress
}

export const hasPendingCards = (track, cardProgress, branchProgress, ctx = {}) => {
  if (!track) return false

  if (track.steps) {
    for (const page of collectActivePages(track.steps, branchProgress, ctx)) {
      const pageId = getPageId(page)

      for (const card of page.cards || []) {
        if (!isCardVisible(card, ctx)) continue
        const status = getCardStatus(cardProgress, pageId, card.id)
        if (status !== CARD_COMPLETED && status !== CARD_SKIPPED) {
          return true
        }
      }
    }
  }

  for (const rawEntry of track.globalCards || []) {
    const entry = getGlobalCardEntry(rawEntry)
    if (!entry?.card) continue
    if (!isGlobalCardVisible(entry, ctx)) continue
    if (ctx.pathname && !matchesRouteFilters(ctx.pathname, getRouteFilters(entry))) continue

    const status = getCardStatus(cardProgress, GLOBAL_PAGE_ID, entry.card.id)
    if (status !== CARD_COMPLETED && status !== CARD_SKIPPED) {
      return true
    }
  }

  return false
}

export const buildInitialBranchProgress = (track, ctx = {}) => {
  const branchProgress = {}

  for (const step of (track?.steps || []).filter(Boolean)) {
    if (!isBranchFork(step)) continue

    const resolvedBranchId = getForkActiveBranchId(step, branchProgress, ctx)
    if (resolvedBranchId) {
      branchProgress[step.id] = {
        status: BRANCH_ACTIVE,
        activeBranchId: resolvedBranchId,
      }
      branchProgress[resolvedBranchId] = { status: BRANCH_ACTIVE }
    }
  }

  return branchProgress
}
